import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ConfirmationDialogComponent,
  ConfirmationDialogModel,
} from '../../components/confirmation-dialog/confirmation-dialog.component';

/**
 * ConfirmationService centralises the *mechanism* of an irreversible-action
 * confirmation: open the shared `ConfirmationDialogComponent` with the house
 * config and report whether the user confirmed. It mirrors `ErrorDialogService`
 * — a `providedIn: 'root'` wrapper around `MatDialog` — and is deliberately
 * domain-free: its only input is the existing `ConfirmationDialogModel`, so it
 * cannot leak per-action meaning. Share the mechanism, not the meaning.
 *
 * Content convention (built by each caller, NOT here): a confirmation's
 * `details` list is ordered **WHO → WHAT → WHEN** — recipient first (who is
 * this going to), then amount/what, then dates, then any action-specific
 * extras. Keeping content in the caller is exactly what stops this shared seam
 * from turning leaky; consistency is a documented convention enforced by review,
 * not by types.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmationService {
  constructor(private readonly dialog: MatDialog) {}

  /**
   * Open the shared confirmation dialog for `model` and emit `true` when the
   * user confirms, `false` on cancel or dismiss. `config` overrides the house
   * config (`maxWidth: '480px'`); `data` is always the model, so callers cannot
   * accidentally clobber it.
   */
  confirm(model: ConfirmationDialogModel, config?: MatDialogConfig): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialogComponent, { maxWidth: '480px', ...config, data: model })
      .afterClosed()
      .pipe(map((result) => !!result));
  }
}
